<template>
  <div class="tw-mb-14 md:tw-mb-0">
    <base-content class="tw-pb-0">
      <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-border-b tw-pb-2">My Plans</h1>
    </base-content>
    <base-content>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4" v-if="isLoading">
        <div v-for="i in 3" :key="i" class="tw-bg-white tw-shadow-sm tw-rounded-md">
          <div class="tw-py-2.5 tw-px-4 sm:tw-py-4 tw-flex">
            <base-skeleton-loader class="tw-h-10 tw-w-10 tw-rounded" />
            <div class="tw-ml-3 tw-flex-grow">
              <div class="tw-flex tw-justify-between">
                <base-skeleton-loader class="tw-w-24 tw-h-3 tw-mt-0.5" />
                <base-skeleton-loader class="tw-w-12 tw-h-4 tw-rounded-full" />
              </div>
              <base-skeleton-loader class="tw-w-12 tw-h-2 tw-mt-1.5" />
            </div>
          </div>
          <div class="tw-border-t tw-border-gray-100 tw-flex tw-flex-grow">
            <div class="tw-border-r tw-border-gray-100 tw-py-2.5 tw-px-4 sm:tw-px-6 tw-flex-1 tw-text-center tw-flex tw-flex-col tw-justify-center">
              <base-skeleton-loader class="tw-h-2 tw-w-8 tw-mx-auto tw-mt-1" />
              <base-skeleton-loader class="tw-h-4 tw-w-12 tw-mt-2 tw-mx-auto tw-mb-1" />
            </div>
            <div class="tw-py-2.5 tw-px-4 sm:tw-px-6 tw-flex-1 tw-text-center tw-flex tw-flex-col tw-justify-center">
              <base-skeleton-loader class="tw-h-2 tw-w-8 tw-mx-auto tw-mt-1" />
              <base-skeleton-loader class="tw-h-4 tw-w-12 tw-mt-2 tw-mx-auto tw-mb-1" />
            </div>
          </div>
        </div>
      </div>
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4 tw-text-black" v-else>
        <router-link
          v-for="plan in plans"
          :key="plan.userBusinessPlanId"
          class="tw-bg-white tw-shadow-sm tw-rounded-md tw-flex tw-flex-col tw-transform tw-transition-all tw-duration-200 tw-ease-in-out hover:tw-scale-105 hover:tw-no-underline hover:tw-shadow-md"
          :to="getRoute(plan)"
        >
          <div class="tw-py-2.5 tw-px-4 sm:tw-py-4 tw-flex">
            <div class="tw-w-10 tw-h-10 tw-flex-shrink-0">
              <Avatar :plan-id="plan.businessPlanId" :business-id="plan.businessId" avatar-type="plan" :editable="false" :rounded="false" />
            </div>
            <div class="ml-3 tw-flex-grow">
              <div class="tw-flex tw-justify-between tw-items-start">
                <div class="tw-leading-5 tw-text-black">{{ plan.planName }}</div>
                <base-badge :color="plan.status === 'Active' ? 'green' : 'gray'" size="sm" class="tw-flex-shrink-0 tw-leading-4 ml-1">{{ plan.status }}</base-badge>
              </div>
              <div class="tw-text-xs tw-text-gray-500 tw--mt-0.5">{{ plan.businessName }}</div>
            </div>
          </div>
          <div class="tw-border-t tw-border-gray-100 tw-flex tw-flex-grow">
            <div class="tw-border-r tw-border-gray-100 tw-py-2.5 tw-px-4 sm:tw-px-6 tw-flex-1 tw-text-center tw-flex tw-flex-col tw-justify-center">
              <div class="tw-text-gray-500 tw-text-xs tw-font-normal">Credit Balance</div>
              <div class="tw-text-primary">{{ plan.itemCategories.map((x) => x.creditBalance).join(" / ") }}</div>
            </div>
            <div class="tw-py-2.5 tw-px-4 sm:tw-px-6 tw-flex-1 tw-text-center tw-flex tw-flex-col tw-justify-center">
              <div class="tw-text-gray-500 tw-text-xs tw-font-normal">You Saved</div>
              <div class="tw-text-primary">
                <MoneyFormat :value="plan.currentSavingsInCents" :locale="locale" :currency-code="plan.currency" subunits-value></MoneyFormat>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </base-content>
    <div v-if="prescriptions.length">
      <base-content class="tw-pb-0">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-border-b tw-pb-2">My Prescriptions</h1>
      </base-content>
      <base-content>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-4">
          <div v-for="prescription in prescriptions" :key="prescription.userBusinessPlanId" class="tw-bg-white tw-shadow-sm tw-rounded-md">
            <div class="tw-py-2.5 tw-px-4 sm:tw-py-4">
              <div class="tw-flex tw-justify-between tw-items-start">
                <div class="tw-leading-5">{{ prescription.programName }}</div>
                <base-badge :color="prescription.isDeleted ? 'gray' : 'green'" size="sm" class="tw-flex-shrink-0 tw-leading-4 ml-1">
                  {{ !prescription.isDeleted ? "Active" : "Inactive" }}
                </base-badge>
              </div>
              <div class="tw-text-xs tw-text-gray-500 tw--mt-0.5">{{ prescription.businessName }}</div>
            </div>
          </div>
        </div>
      </base-content>
    </div>
  </div>
</template>

<script>
import Avatar from "@/components/Avatar";
import MoneyFormat from "vue-money-format";

export default {
  components: { Avatar, MoneyFormat },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      plans: [],
      prescriptions: [],
      arePlansLoading: false,
      arePrescriptionsLoading: false,
    };
  },
  computed: {
    isLoading() {
      return this.arePlansLoading || this.arePrescriptionsLoading;
    },
    locale() {
      return window.navigator.language;
    },
  },
  created() {
    this.getPlans();
    this.getPrescriptions();
  },
  methods: {
    async getPlans() {
      try {
        this.arePlansLoading = true;
        const response = await this.$axios.get(`/users/${this.userId}/plans`);
        this.plans = response.data;
      } finally {
        this.arePlansLoading = false;
      }
    },
    async getPrescriptions() {
      try {
        this.arePrescriptionsLoading = true;
        const response = await this.$axios.get(`/users/${this.userId}/prescriptions`);
        this.prescriptions = response.data;
      } finally {
        this.arePrescriptionsLoading = false;
      }
    },
    getRoute(plan) {
      if (plan.status === "Awaiting Payment") {
        return `/users/${this.userId}/plans/${plan.userBusinessPlanId}/activate`;
      } else {
        return `/users/${this.userId}/businesses/${plan.businessId}/plans/${plan.userBusinessPlanId}/details`;
      }
    },
  },
};
</script>
